import { Grid } from 'semantic-ui-react';
import { RoleDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { noop } from 'utils/functionUtils';
import DeleteActionButton from 'components/DeleteActionButton';
import LoaderComponent from 'components/LoaderComponent';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { deleteRole, findRoleById, searchRole } from 'services/roleServices';
import UpsertRoleForm from 'components/user-settings/UpsertRoleForm';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SearchResultComponent from 'components/SearchResultComponent';

interface SearchRoleQuery {
  query: string;
}

interface RoleFieldProps extends FormState {
  values: Partial<SearchRoleQuery>
}

interface Props extends MessageSourceProps {
}

interface State {
  foundRoles: Array<RoleDto>,
  rolesLoaded: boolean,
  role?: RoleDto,
  roleId?: string,
  addEditModalOpen: boolean,
  errorMessage?: string,
  searchValues: string,
  cancelTokenSource: CancelTokenSource,
}

class RolesView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundRoles: [],
      rolesLoaded: false,
      role: undefined,
      roleId: undefined,
      addEditModalOpen: false,
      searchValues: "",
      cancelTokenSource
    };
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (roleId?: string) => {

    const {cancelTokenSource} = this.state;

    if (roleId) {
      findRoleById(roleId, cancelTokenSource).then(response => {
        this.showModal(roleId, response);
      }).catch(noop);
    }
  };

  showModal = (roleId?: string, role?: RoleDto) => {

    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        role: role,
        roleId: roleId
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      roleId: undefined,
      role: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        rolesLoaded: true
      })
    };

    searchRole(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundRoles: response
          });
        })
        .catch(this.handleError)
        .finally(onFinally);

    this.hideModal();
  };

  deleteRole = (roleId: string) => {
    const {cancelTokenSource} = this.state;
    deleteRole(roleId, cancelTokenSource).then(() => {
      this.setState(({foundRoles}: State) => ({
        foundRoles: foundRoles.filter((role: RoleDto) => role.roleId != roleId)
      }))
    }).catch(this.handleError)
  };

  handleRoleChange = debounce(({values}: RoleFieldProps) => {
    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;
    this.setState({rolesLoaded: false});
    const query = values && values.query ? values.query : "";

    const onFinally = () => {
      this.setState({
        rolesLoaded: true
      })
    };

    searchRole(query, cancelTokenSource)
        .then(response => {
          this.setState({
            foundRoles: response,
            searchValues: query
          });
        })
        .catch(this.handleError)
        .finally(onFinally);
  }, 300);

  handleError() {
    const {getMessage} = this.props;

    const errorMessage = getMessage('error.general');
    this.setErrorMessage(errorMessage)
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {
    const {rolesLoaded, errorMessage} = this.state;
    const {getMessage} = this.props;

    return (
        <PraxStyledSegment>
          {errorMessage
          && (
              <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                {errorMessage}
              </StyledErrorMessage>
          )
          }
          <div>
            {this.renderSearchRolesSegment()}
          </div>
          <StyledViewTableRow>
            {!rolesLoaded
                ? <LoaderComponent message={getMessage("role.loading")}/>
                : this.renderRolesSegment()}
          </StyledViewTableRow>
          {this.renderUpsertRoleModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchRolesSegment = (): JSX.Element => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{}}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundRoles} = this.state;

    const numResults = foundRoles.length;

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="query"
                  fluid
                  component={Input}
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleRoleChange}/>
        </PxGrid>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("role.confirmDelete")}
              onConfirm={() => this.deleteRole(cellData)}
          />
        </React.Fragment>
    );
  };

  rolesRowGetter = ({index}: any) => {
    const {foundRoles} = this.state;

    Object.assign(foundRoles[index], {index: index + 1});

    return foundRoles[index]
  };

  foundRolesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundRoles} = this.state;
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundRoles[index].roleId)}
        >
          {columns}
        </div>
    );
  };

  renderRolesSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundRoles} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundRoles.length}
            rowGetter={this.rolesRowGetter}
            rowRenderer={this.foundRolesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 450,
                label: (getMessage("roleId")),
                dataKey: "roleId"
              },
              {
                width: 450,
                label: (getMessage("name")),
                dataKey: "name"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("description")),
                dataKey: "description"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "roleId",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  renderUpsertRoleModal = (): React.ReactNode => {

    const {addEditModalOpen, role, roleId} = this.state;

    return (
        addEditModalOpen &&
        <UpsertRoleForm
            role={role}
            roleId={roleId}
            modalOpen={addEditModalOpen}
            onCancel={this.hideModal}
            onSave={this.onSaveUpsertForm}
        />
    );
  }

}

export default withMessages(RolesView);
